import MuiFormControl from '@mui/material/FormControl';
import MuiFormLabel from '@mui/material/FormLabel';
import MuiTextField from '@mui/material/TextField';
import { alpha, type Theme } from '@mui/material/styles';
import { forwardRef, useId, type ChangeEvent, type KeyboardEvent, type ReactNode } from 'react';
import { FONT_FAMILY_MONO, fontWeights } from '../../theme';

export interface TextFieldProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  // Visible label, rendered ABOVE the control and wired to the input by a real `<label for>`.
  // Prefer it over aria-label wherever the label is on screen — one text, not two to keep in sync.
  label?: string;
  // 'plain' is the field that IS a dialog's header: it draws no frame of its own, because the rule
  // under the header already separates it from the body and a box inside it draws the same edge
  // twice. MUI's standard variant with the underline off, set at the header type step — not a
  // second wrapper, since the control, its adornments and its keyboard contract are unchanged.
  //
  // 'search' is the field that IS the product's search box: a filled, frameless control on the
  // brand grey the tab strip already uses, which owns the tone of every glyph inside it. It rides
  // MUI's outlined input with the outline zeroed rather than MUI's `filled`, which would bring its
  // own background plus hover and focus rules to override, round only its top two corners, and
  // need `hiddenLabel` before its padding is symmetric — five overrides to reach where one gets us,
  // and a third JSX branch besides (see the union note in `common` below).
  variant?: 'outlined' | 'plain' | 'search';
  placeholder?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  fullWidth?: boolean;
  disabled?: boolean;
  // Takes focus on mount, for the first field of a dialog that opens OVER the control which
  // triggered it — otherwise the keyboard lands on <body> and the first stop is the «×», so the
  // fastest key press closes the thing that just opened. Declarative even though this wrapper does
  // forward a ref: reaching the input through the ref costs every caller an effect, and here the
  // ref is usually already spoken for (the shell holds the search box's to focus it on ⌘K).
  autoFocus?: boolean;
  // Renders the asterisk AND announces the requirement, the same two jobs `Select` names — and
  // cheaper here than there: MUI writes the attribute onto the NATIVE <input>, which is what
  // assistive tech reads the state from, so nothing has to hand-route `aria-required` the way a
  // role="combobox" div did. Both halves still have to be asked for separately: the outer
  // FormControl draws the glyph, the field carries the attribute.
  required?: boolean;
  // A fixed-height box with internal scroll, deliberately NOT `minRows`/`maxRows`: the one caller
  // pastes waves of a thousand ids, and an auto-growing field would render a card a thousand lines
  // tall. Forwarded straight to MUI, which renders a <textarea> through the same InputBase.
  multiline?: boolean;
  rows?: number;
  // Enforced by the native element, which cuts what is typed AND what is pasted. No counter here:
  // whether the cap is worth showing is the caller's call.
  maxLength?: number;
  // The field's own description — the input rule and, for the batch card, the reject readout. A
  // sibling <Text> would render the same pixels with no `aria-describedby`: the description belongs
  // to the control, not beside it.
  helperText?: ReactNode;
  // The face `docs/DESIGN.md` §4 assigns to ids. It exists as a prop BECAUSE this wrapper takes no
  // `sx`: a view has no other way to ask for it. In a column of ids it also does a second job — a
  // pasted `LOC-8433` is visible as the outlier it is at a glance.
  monospace?: boolean;
  // Which step of the type scale the control's TEXT is set in. `body1` (MUI's own default for every
  // `InputBase`) unless a caller asks otherwise; `body2` is for a field that REPLACES body2 copy in
  // place — the «Usuario» ficha's in-line edit mode, where a 16px control in a column of 14px
  // values re-types the whole column one step bigger. `size` does NOT cover this: it trims padding
  // and leaves the type step alone. It exists as a prop because this wrapper takes no `sx`
  // (ADR 0004), and as a union of theme step names rather than a boolean so the next step it needs
  // is an entry and not a second flag.
  textVariant?: 'body1' | 'body2';
  // Marks the value as rejected. MUI tints the frame and, through `InputBase`, writes
  // `aria-invalid` onto the input — the only programmatic way to say «this value was rejected».
  //
  // The tint stays on the FRAME: `error.main` (#F26279) measures 3.09:1 on white, which clears the
  // 3:1 a border needs and falls short of the 4.5:1 a 12px sentence does. So the helper keeps the
  // body tone (see `helperSx`) and the meaning is carried by the message, never by the colour.
  error?: boolean;
  'aria-label'?: string;
}

// The label sits ABOVE the control rather than floating into the outline notch, so the field's
// border stays unbroken (the Figma treatment for the address-filter dialog's three fields). Kept
// identical to the Select wrapper's caption — the two stand in the same form, and a caption a step
// larger on one of them would misalign the pair. Same reason both wrappers hardcode size="small".
const ABOVE_LABEL_SX = {
  typography: 'body2',
  // 8px, the grid's own step. The caption sits ABOVE the control rather than on it, so nothing but
  // this margin separates the two, and the longest of these labels is a full sentence — the batch
  // card's input rule — which at 4px read as the field's first line instead of as its caption.
  // Moved in BOTH wrappers at once: the two stand side by side in the address-filter dialog.
  mb: 1,
  // A label ON the control doubles as a focus indicator; one above it is a caption, and the
  // control's own focus ring already says where focus is. This label hears about focus from nobody
  // anyway (it sits outside any FormControl) — pinned so the Select wrapper's caption, which does,
  // behaves the same rather than tinting itself purple.
  '&.Mui-focused': { color: 'text.secondary' },
} as const;

// The header step rather than a size of its own: what the plain field replaces is a DialogTitle,
// so it is set where a title would be. Targets the inner <input> — MUI's own font-size lives on
// the InputBase root, and a value on the wrapper would be the one the cascade drops.
const plainSx = (theme: Theme) => ({
  // The glyphs stand off the text beside them. MUI lays adornments out as flex children of the
  // InputBase with no gap of its own — an outlined field gets away with that because its own left
  // padding does the work, and a field with no box has nothing to fall back on: the magnifier ends
  // up against the first letter typed.
  '& .MuiInputBase-root': { gap: theme.spacing(2) },
  '& .MuiInputBase-input': { fontSize: theme.typography.h6.fontSize },
});

const searchSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.default,
    // The shape scale, not the spacing one: both land on 8px here, and only one of them means
    // «radius». Same value the dialogs, the alerts and the tab strips take.
    borderRadius: 2,
    // MUI hangs an outlined field's horizontal padding on the root and its vertical padding on the
    // input, so the two insets are set in two places on purpose.
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    // The ring is what replaces the outline as the focus indicator. Inset, so it never displaces
    // the control or the chrome beside it the way a grown border would. Nested here rather than at
    // the top level: there, the class it hangs off would be the FormControl's, which never takes it.
    '&.Mui-focused': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
      // Said twice on purpose: MUI qualifies its own focused notch with .Mui-focused, which
      // outranks the unqualified zero no matter what order the two load in.
      '& .MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
    },
  },
  // Zeroed in every state, focus included — the width and not the shorthand, because MUI's own
  // rule is a width (1px, 2px while focused) and that is the declaration this has to beat.
  '& .MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
  // Everything the field HOLDS, scoped to the input control and not to an adornment wrapper: the
  // call sites pass their glyphs bare, so MUI creates no wrapper of its own to hang these off.
  '& .MuiInputBase-root': {
    // The glyphs stand off the text between them. MUI zeroes BOTH of an outlined input's horizontal
    // paddings as soon as adornments exist, and lays a bare glyph out as a flex child with no margin
    // — so without this the magnifier touches the first letter typed and the trigger touches the
    // last. Measured off the design at 16px. Same rule, same reason, as the plain variant.
    gap: theme.spacing(2),
    // One rule for every glyph the field holds — the magnifier AND the search-type trigger's pin
    // and chevron, which ride in the end adornment. A glyph beside its own words reads a step below
    // them, and this is the only place that knows all of them are in the same box.
    '& svg': { color: alpha(theme.palette.text.primary, 0.6) },
    // A menu trigger in here names what the agent is searching, so it is the one piece of text in
    // the field set above the term itself. Its weight belongs to the field for the same reason its
    // open-state fill does: the façade Button accepts no style from a call site, and it grows no
    // prop for a single caller.
    '& .MuiButton-root': { fontWeight: fontWeights.bold },
    // MUI pulls a button's end icon toward the edge with a negative right margin, on the assumption
    // that the button's own padding is the last thing before open space. In here it is not: the
    // field's inset comes after it, so the pull lands the chevron against the field's right edge.
    // Positive, so the glyph keeps the air the design gives it.
    '& .MuiButton-endIcon': { marginRight: theme.spacing(0.5) },
    // A control in here that opens a menu says so with a fill, not with a box drawn inside the box.
    // It hangs off the aria state the trigger already writes, so the look and what assistive tech is
    // told cannot drift apart — and no façade prop has to exist to carry it.
    '& .MuiButton-root[aria-expanded="true"]': {
      backgroundColor: alpha(theme.palette.text.primary, 0.08),
    },
  },
  // Targets the inner <input>: MUI's own font-size lives on the InputBase root, so a value on the
  // wrapper is the one the cascade drops. Same reason the plain variant sets it here.
  //
  // The 34px height is DERIVED, not declared: body2 at MUI's 1.4375em input height is 20.125px,
  // plus these 7px above and below. A declared 34 would fight the derived one the first time the
  // type scale moves.
  '& .MuiInputBase-input': {
    fontSize: theme.typography.body2.fontSize,
    paddingTop: '7px',
    paddingBottom: '7px',
  },
});

// MUI renders a helper at `text.secondary`, which in this theme is #ACA5BB — 2.37:1 on white, below
// AA for body text. A helper is a rule the reader has to act on, not a caption they may skip, so it
// keeps the body tone. Same decision, same reason, as the page intro and the timeline.
const helperSx = {
  '& .MuiFormHelperText-root': { color: 'text.primary' },
  // Said again for the error state, because MUI's own `.MuiFormHelperText-root.Mui-error` matches
  // at the same weight as the unqualified rule above and would win on injection order alone. The
  // tone is deliberately unchanged: see the `error` prop for why the tint stops at the frame.
  '& .MuiFormHelperText-root.Mui-error': { color: 'text.primary' },
} as const;

const monospaceSx = { '& .MuiInputBase-input': { fontFamily: FONT_FAMILY_MONO } } as const;

// On the InputBase ROOT and not on the inner input: the root is where MUI sets `body1`, and the
// adornments live there too — a step applied to the input alone would leave a magnifier sized for
// the other scale beside it.
const bodyTwoSx = { '& .MuiInputBase-root': { typography: 'body2' } } as const;

// forwardRef: callers focus the real <input> through the wrapper (e.g. the shell
// focuses the search box on ⌘K). onChange emits the string value, not the event,
// so callers never touch MUI's synthetic event.
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    value,
    onChange,
    onKeyDown,
    label,
    variant = 'outlined',
    placeholder,
    startAdornment,
    endAdornment,
    fullWidth = false,
    disabled = false,
    autoFocus = false,
    required = false,
    multiline = false,
    rows,
    maxLength,
    helperText,
    monospace = false,
    textVariant = 'body1',
    error = false,
    'aria-label': ariaLabel,
  },
  ref,
) {
  const isPlain = variant === 'plain';
  const isSearch = variant === 'search';

  // Own id rather than MUI's generated one: the label is rendered by this wrapper, outside the
  // control, so `htmlFor` has to point at something this wrapper knows the name of.
  const inputId = useId();

  // Everything the two variants share, spread rather than repeated. It stays a plain object so
  // the JSX below picks ONE branch of MUI's variant union: `variant={cond ? a : b}` widens the
  // prop to `'standard' | 'outlined'`, which matches no member of that union and fails to compile.
  const common = {
    value,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value);
    },
    fullWidth,
    disabled,
    autoFocus,
    inputRef: ref,
    // `label` is deliberately NOT forwarded: MUI would float it into the outline and cut a notch
    // in the border to seat it. It is rendered as a sibling below instead.
    // `id` is what MUI hangs BOTH associations off: the rendered <label for> and the helper text's
    // `aria-describedby`. Either one alone is reason enough to mint it.
    ...(label !== undefined || helperText !== undefined ? { id: inputId } : {}),
    ...(placeholder !== undefined ? { placeholder } : {}),
    ...(helperText !== undefined ? { helperText } : {}),
    ...(error ? { error } : {}),
    ...(required ? { required } : {}),
    ...(multiline ? { multiline } : {}),
    ...(rows !== undefined ? { rows } : {}),
  };

  const adornments = {
    ...(startAdornment !== undefined ? { startAdornment } : {}),
    ...(endAdornment !== undefined ? { endAdornment } : {}),
  };

  const htmlInput = {
    ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
    // Bound to the input, not the control root: MUI spreads unknown props onto the root
    // FormControl, where an adornment control's keydowns bubble in too.
    ...(onKeyDown ? { onKeyDown } : {}),
    ...(maxLength !== undefined ? { maxLength } : {}),
  };

  // Composed rather than replaced: `search`, the helper tone and the mono face are orthogonal, and
  // a field can want any two of them at once.
  const outlinedSx = [
    ...(isSearch ? [searchSx] : []),
    ...(helperText !== undefined ? [helperSx] : []),
    ...(monospace ? [monospaceSx] : []),
    ...(textVariant === 'body2' ? [bodyTwoSx] : []),
  ];

  const field = isPlain ? (
    <MuiTextField
      {...common}
      variant="standard"
      size="medium"
      sx={[
        plainSx,
        ...(helperText !== undefined ? [helperSx] : []),
        ...(monospace ? [monospaceSx] : []),
        ...(textVariant === 'body2' ? [bodyTwoSx] : []),
      ]}
      slotProps={{ input: { ...adornments, disableUnderline: true }, htmlInput }}
    />
  ) : (
    <MuiTextField
      {...common}
      variant="outlined"
      size="small"
      {...(outlinedSx.length > 0 ? { sx: outlinedSx } : {})}
      slotProps={{ input: adornments, htmlInput }}
    />
  );

  // Unlabelled fields (every header search box in the product) render exactly as they did: no
  // wrapper element, no label node. The stack only exists where there is a caption to stack.
  if (label === undefined) return field;

  return (
    // `fullWidth` is the field's own concern, but MUI implements it as width:100% — which measures
    // this box, so the box has to fill its parent too or the field stops at its content width.
    <MuiFormControl fullWidth={fullWidth} disabled={disabled} required={required} size="small">
      <MuiFormLabel htmlFor={inputId} sx={ABOVE_LABEL_SX}>
        {label}
      </MuiFormLabel>
      {field}
    </MuiFormControl>
  );
});
